import { i18n } from '@/app/i18n';
import { sentryService } from '@/services/telemetry/sentry';

/**
 * Locale-aware currency formatter via `Intl.NumberFormat`. Reads the current
 * locale from the global `i18n` instance so callers never have to pass it in.
 *
 * Defensive against the real shapes we've seen in production:
 * - `null` / `undefined` / `NaN` sum → 0 (never render "null" or "NaN")
 * - `null` / `undefined` / empty currency → "eur" fallback (Intl.NumberFormat
 *   throws `RangeError: Invalid currency code: null` without this guard)
 * - Unrecognized currency ISO code → plain `"EUR 12.34"` string fallback
 */
export function formatSum(
  sum: number | null | undefined,
  currency: string | null | undefined
): string {
  const safeSum = sum != null && Number.isFinite(Number(sum)) ? Number(sum) : 0;
  const safeCurrency =
    currency && typeof currency === 'string' ? currency : 'eur';
  const localeCode = i18n.global.locale.value || 'en-GB';

  if (!currency || typeof currency !== 'string') {
    sentryService.addBreadcrumb(
      'localization',
      `formatSum called with invalid currency: ${String(currency)}, falling back to "${safeCurrency}"`,
      {
        original_currency: String(currency),
        sum: safeSum,
        fallback: safeCurrency,
      },
      'warning'
    );
  }

  try {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: safeCurrency,
    }).format(safeSum);
  } catch (error) {
    sentryService.captureException(error, {
      tags: {
        error_type: 'localization_format_error',
        locale: localeCode,
      },
      extra: {
        sum: safeSum,
        currency: safeCurrency,
        original_currency: String(currency),
        locale_code: localeCode,
      },
      level: 'warning',
    });
    return `${safeCurrency.toUpperCase()} ${safeSum.toFixed(2)}`;
  }
}
